import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { Portal } from "../portal";
import { Overlay } from "../overlay";
import { OverflowHidden } from "../side-effects/overflow-hidden";
import { CloseIcon } from "@core-ui/components/close-icon";
import { withStyles } from "@core-ui/helpers/WithStyles";
import styles from "./BottomSheet.scss";
import { setElementHeightInDom, resetElementHeightInDom, STYLE_SHEET_VAR } from "@core-ui/helpers/ElementHeight";

export interface BottomSheetChildrenProps {
    close: VoidFunction;
}

interface BottomSheetProps {
    onClose: VoidFunction;
    children: (props: BottomSheetChildrenProps) => ReactNode;
    hideOnOverlayClick?: boolean;
    containerClass?: string;
    containerDataTestId?: string;
    closeButtonDataTestId?: string;
    onClickCloseButton?: VoidFunction;
    hideCloseIcon?: boolean;
    closeButtonClassName?: string;
    closeIcon?: ReactNode;
}

const CLOSE_ICON_DIMENSION = 12;

function BottomSheet({
    onClose,
    children,
    containerClass,
    closeButtonDataTestId,
    hideOnOverlayClick,
    containerDataTestId,
    onClickCloseButton,
    hideCloseIcon,
    closeButtonClassName,
    closeIcon,
}: BottomSheetProps): React.ReactElement {
    const [isVisible, setVisible] = useState(true);

    const bottomSheetRef = useRef<HTMLDivElement>(null);

    const closeBS = useCallback(() => setVisible(false), [setVisible]);
    const handleOverlayClick = useCallback(() => {
        if (!hideOnOverlayClick) {
            return;
        }
        closeBS();
    }, [hideOnOverlayClick, closeBS]);

    const _onClose = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            if (typeof onClickCloseButton === "function") {
                onClickCloseButton();
            }
            closeBS();
        },
        [closeBS, onClickCloseButton],
    );

    const onRest = useCallback(() => {
        //TO set/reset bottomsheet height whenever animation comes to a standstill
        const bottomSheetHeight = bottomSheetRef?.current?.clientHeight ?? 0;
        setElementHeightInDom(bottomSheetHeight, STYLE_SHEET_VAR.bottomSheet);
        if (!isVisible) {
            resetElementHeightInDom(STYLE_SHEET_VAR.bottomSheet);
            onClose();
        }
    }, [isVisible, onClose]);

    const containerClassName = classNames(styles.container, containerClass, {
        [styles.containerUx4]: true,
        [styles.containerShow]: isVisible,
        [styles.containerHide]: !isVisible,
    });

    const closeButtonClass = classNames(closeButtonClassName, styles.closeIcon);

    //To trigger on component unmount
    useEffect(() => {
        return (): void => {
            resetElementHeightInDom(STYLE_SHEET_VAR.bottomSheet);
        };
    }, []);

    const onBottomSheetClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
    }, []);

    const closeIconElement = useMemo(
        () =>
            closeIcon ? (
                closeIcon
            ) : (
                <CloseIcon
                    dataTestId={closeButtonDataTestId}
                    className={closeButtonClass}
                    onClick={_onClose}
                    size={CLOSE_ICON_DIMENSION}
                />
            ),
        [closeIcon, closeButtonDataTestId, closeButtonClass, _onClose],
    );

    return (
        <Portal>
            <OverflowHidden>
                <div
                    id="payments-bs-container"
                    role="dialog"
                    aria-modal="true"
                    data-testid={containerDataTestId}
                    onClick={onBottomSheetClick}
                >
                    <div
                        id="payments-bs-container-content"
                        className={containerClassName}
                        ref={bottomSheetRef}
                        onAnimationEnd={onRest}
                    >
                        {children({ close: closeBS })}

                        {!hideCloseIcon && closeIconElement}
                    </div>
                    {isVisible && <Overlay onClick={handleOverlayClick} />}
                </div>
            </OverflowHidden>
        </Portal>
    );
}

export default withStyles(BottomSheet, styles);
